// "Who is buying?" for the buyer create surfaces — the record's select (same
// labels, `BUYER_TYPE_LABELS`) with the company rows revealed only for a company
// type, the `visibleWhen` rule the Contact section uses. State, the create-time
// rule and the request fragment live in `buyerTypeCreate.ts`.
//
// Optional: blank is an individual, and an individual sees one extra optional
// select and nothing else.

import { Select } from "@/components/ui/Select";
import { FormField, Input } from "@/design-system/components/ui";
import type { BuyerType } from "@/api/buyers";
import { BUYER_TYPE_LABELS } from "@/pages/buyers/sections/buyerSectionConfig";
import {
  COMPANY_NAME_MAX_LENGTH,
  COMPANY_TITLE_MAX_LENGTH,
  COMPANY_WEBSITE_MAX_LENGTH,
  companyDetailsFieldErrors,
  isCompanyTypeChoice,
  type BuyerTypeCreateState,
} from "./buyerTypeCreate";

const BUYER_TYPE_ORDER: readonly BuyerType[] = [
  "Individual",
  "CompanyOwnedOrLed",
  "InvestmentGroupOrFund",
];

interface BuyerTypeCreateFieldsProps {
  value: BuyerTypeCreateState;
  onChange: (next: BuyerTypeCreateState) => void;
  /** Show the website row with the company rows. Quick add leaves it out. */
  showWebsite?: boolean;
  /** Show the required-field errors — after a save attempt, or always when the
   *  host keeps Save disabled until the form is complete. */
  showErrors?: boolean;
}

export function BuyerTypeCreateFields({
  value,
  onChange,
  showWebsite = true,
  showErrors = false,
}: BuyerTypeCreateFieldsProps) {
  const company = isCompanyTypeChoice(value.buyerType);
  const errors = showErrors ? companyDetailsFieldErrors(value) : {};
  const set = (patch: Partial<BuyerTypeCreateState>) => onChange({ ...value, ...patch });

  return (
    <>
      <FormField label="Who is buying?" helperText="Leave blank for a buyer purchasing personally.">
        <Select
          value={value.buyerType}
          onChange={(e) => set({ buyerType: e.target.value as BuyerType | "" })}
        >
          <option value="">—</option>
          {BUYER_TYPE_ORDER.map((t) => (
            <option key={t} value={t}>
              {BUYER_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
      </FormField>
      {company && (
        <>
          <FormField label="Acquiring company" required error={errors.companyName}>
            <Input
              value={value.companyName}
              invalid={!!errors.companyName}
              maxLength={COMPANY_NAME_MAX_LENGTH}
              onChange={(e) => set({ companyName: e.target.value })}
            />
          </FormField>
          <FormField label="Title at the company" required helperText="e.g. CEO" error={errors.title}>
            <Input
              value={value.title}
              invalid={!!errors.title}
              maxLength={COMPANY_TITLE_MAX_LENGTH}
              onChange={(e) => set({ title: e.target.value })}
            />
          </FormField>
          {showWebsite && (
            <FormField label="Company website" helperText="Optional — https://">
              <Input
                value={value.website}
                maxLength={COMPANY_WEBSITE_MAX_LENGTH}
                onChange={(e) => set({ website: e.target.value })}
              />
            </FormField>
          )}
        </>
      )}
    </>
  );
}
